// Copyright 2025, University of Maryland, All Rights Reserved

/**
 * Shared print infrastructure for all application report renderers.
 * Provides base CSS, HTML document wrapper, and print window launcher.
 */

import { escapeHtml } from './formatters'
import { PRINT_COLORS } from './colors'

// ============================================================================
// Base Print Styles
// ============================================================================

/**
 * Core CSS for print output.
 * Uses DM Serif Display for headings and DM Sans for body text.
 */
export const PRINT_BASE_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=DM+Serif+Display&display=swap');

  *, *::before, *::after {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }

  html, body {
    width: 100%;
    background: ${PRINT_COLORS.surface};
    color: ${PRINT_COLORS.ink};
    font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif;
    font-size: 11pt;
    line-height: 1.6;
    -webkit-font-smoothing: antialiased;
  }

  body {
    padding: 2.5rem 3rem;
    max-width: 900px;
    margin: 0 auto;
  }

  /* Typography */
  h1 {
    font-family: 'DM Serif Display', Georgia, serif;
    font-size: 1.75rem;
    font-weight: 400;
    letter-spacing: -0.02em;
    color: ${PRINT_COLORS.ink};
    margin: 0 0 1.5rem;
    padding-bottom: 0.75rem;
    border-bottom: 2px solid ${PRINT_COLORS.ink};
  }

  h2 {
    font-family: 'DM Serif Display', Georgia, serif;
    font-size: 1.25rem;
    font-weight: 400;
    color: ${PRINT_COLORS.ink};
    margin: 0;
    page-break-after: avoid;
    break-after: avoid;
  }

  h3 {
    font-family: 'DM Sans', sans-serif;
    font-size: 1rem;
    font-weight: 600;
    color: ${PRINT_COLORS.ink};
    margin: 0;
    page-break-after: avoid;
    break-after: avoid;
  }

  p {
    margin: 0;
  }

  /* Cards */
  .card {
    background: ${PRINT_COLORS.surface};
    border: 1px solid ${PRINT_COLORS.border};
    border-radius: 8px;
    padding: 1.25rem 1.5rem;
    margin-bottom: 1rem;
    page-break-inside: avoid;
    break-inside: avoid;
  }

  .card--elevated {
    box-shadow: 0 1px 3px rgba(0,0,0,0.04), 0 2px 8px rgba(0,0,0,0.02);
  }

  .card--muted {
    background: ${PRINT_COLORS.surfaceMuted};
    border-color: ${PRINT_COLORS.borderLight};
  }

  /* Badges */
  .badge {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    font-family: 'DM Sans', sans-serif;
    font-size: 0.7rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    padding: 0.2rem 0.5rem;
    border-radius: 4px;
    border: 1px solid;
  }

  .badge--high {
    background: ${PRINT_COLORS.high.bg};
    border-color: ${PRINT_COLORS.high.border};
    color: ${PRINT_COLORS.high.text};
  }

  .badge--medium {
    background: ${PRINT_COLORS.medium.bg};
    border-color: ${PRINT_COLORS.medium.border};
    color: ${PRINT_COLORS.medium.text};
  }

  .badge--low {
    background: ${PRINT_COLORS.low.bg};
    border-color: ${PRINT_COLORS.low.border};
    color: ${PRINT_COLORS.low.text};
  }

  .badge--required {
    background: ${PRINT_COLORS.required.bg};
    border-color: ${PRINT_COLORS.required.border};
    color: ${PRINT_COLORS.required.text};
  }

  .badge--critical {
    background: ${PRINT_COLORS.critical.bg};
    border-color: ${PRINT_COLORS.critical.border};
    color: ${PRINT_COLORS.critical.text};
  }

  .badge--preferred {
    background: ${PRINT_COLORS.preferred.bg};
    border-color: ${PRINT_COLORS.preferred.border};
    color: ${PRINT_COLORS.preferred.text};
  }

  .badge--neutral {
    background: ${PRINT_COLORS.surfaceMuted};
    border-color: ${PRINT_COLORS.border};
    color: ${PRINT_COLORS.inkMuted};
  }

  .badge--accent {
    background: ${PRINT_COLORS.accentLight};
    border-color: #c7d2fe;
    color: ${PRINT_COLORS.accent};
  }

  /* Dots for stats */
  .dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    display: inline-block;
  }

  .dot--high { background: ${PRINT_COLORS.high.dot}; }
  .dot--medium { background: ${PRINT_COLORS.medium.dot}; }
  .dot--low { background: ${PRINT_COLORS.low.dot}; }
  .dot--required { background: ${PRINT_COLORS.required.dot}; }
  .dot--critical { background: ${PRINT_COLORS.critical.dot}; }
  .dot--preferred { background: ${PRINT_COLORS.preferred.dot}; }

  /* Section */
  .section {
    margin-top: 2rem;
  }

  .section__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
    padding-bottom: 0.5rem;
    border-bottom: 1px solid ${PRINT_COLORS.borderLight};
    page-break-after: avoid;
    break-after: avoid;
  }

  .section__count {
    font-family: 'DM Sans', sans-serif;
    font-size: 0.75rem;
    font-weight: 500;
    color: ${PRINT_COLORS.inkLight};
    padding: 0.25rem 0.75rem;
    background: ${PRINT_COLORS.surfaceMuted};
    border-radius: 100px;
  }

  /* Summary Card */
  .summary {
    position: relative;
    background: linear-gradient(135deg, ${PRINT_COLORS.surfaceMuted} 0%, ${PRINT_COLORS.surface} 100%);
    border: 1px solid ${PRINT_COLORS.border};
    border-radius: 10px;
    padding: 1.75rem 2rem;
    margin-bottom: 2rem;
  }

  .summary::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, transparent, ${PRINT_COLORS.accent}40, transparent);
    border-radius: 10px 10px 0 0;
  }

  .summary__title {
    font-family: 'DM Sans', sans-serif;
    font-size: 0.65rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.15em;
    color: ${PRINT_COLORS.inkLight};
    margin-bottom: 0.75rem;
  }

  .summary__headline {
    font-family: 'DM Serif Display', Georgia, serif;
    font-size: 1.5rem;
    font-weight: 400;
    color: ${PRINT_COLORS.ink};
    margin-bottom: 1.25rem;
    line-height: 1.35;
    letter-spacing: -0.01em;
  }

  .summary__badges {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-bottom: 1rem;
  }

  .summary__stats {
    display: flex;
    gap: 1.25rem;
    align-items: stretch;
    margin-bottom: 1.25rem;
  }

  .summary__text {
    font-size: 0.875rem;
    color: ${PRINT_COLORS.inkMuted};
    line-height: 1.75;
    padding-top: 1.25rem;
    border-top: 1px solid ${PRINT_COLORS.borderLight};
  }

  .summary__text strong {
    color: ${PRINT_COLORS.ink};
    font-weight: 600;
  }

  /* Stat Box */
  .stat-box {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 1rem 1.25rem;
    border-radius: 10px;
    border: 1px solid ${PRINT_COLORS.border};
    background: ${PRINT_COLORS.surface};
    min-width: 100px;
    box-shadow: 0 1px 2px rgba(0,0,0,0.02);
  }

  .stat-box--colored {
    border-width: 2px;
  }

  .stat-box__label {
    font-size: 0.6rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: ${PRINT_COLORS.inkLight};
    margin-bottom: 0.375rem;
  }

  .stat-box__value {
    font-family: 'DM Serif Display', Georgia, serif;
    font-size: 2rem;
    font-weight: 400;
    color: ${PRINT_COLORS.ink};
    line-height: 1;
  }

  .stat-box__sub {
    font-size: 0.7rem;
    font-weight: 600;
    margin-top: 0.25rem;
    letter-spacing: 0.02em;
  }

  /* Risk/Priority Stats */
  .stats-list {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 0.5rem;
    padding-left: 0.75rem;
  }

  .stats-row {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .stat-item {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    font-size: 0.75rem;
    font-weight: 600;
    color: ${PRINT_COLORS.inkMuted};
    background: ${PRINT_COLORS.surfaceMuted};
    padding: 0.25rem 0.625rem;
    border-radius: 100px;
  }

  /* Finding Card */
  .finding {
    position: relative;
    border: 1px solid ${PRINT_COLORS.border};
    border-radius: 8px;
    padding: 1.25rem 1.5rem 1.25rem 1.75rem;
    margin-bottom: 1rem;
    background: ${PRINT_COLORS.surface};
    page-break-inside: avoid;
    break-inside: avoid;
  }

  .finding--high::before,
  .finding--medium::before,
  .finding--low::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    border-radius: 8px 0 0 8px;
  }

  .finding--high::before { background: ${PRINT_COLORS.high.dot}; }
  .finding--medium::before { background: ${PRINT_COLORS.medium.dot}; }
  .finding--low::before { background: ${PRINT_COLORS.low.dot}; }

  .finding__header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 0.875rem;
  }

  .finding__meta {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.5rem;
  }

  .finding__id {
    font-family: 'DM Mono', monospace;
    font-size: 0.65rem;
    color: ${PRINT_COLORS.inkLight};
    font-weight: 500;
    letter-spacing: -0.02em;
  }

  .finding__title {
    font-family: 'DM Sans', sans-serif;
    font-size: 1rem;
    font-weight: 600;
    color: ${PRINT_COLORS.ink};
    margin: 0;
    line-height: 1.35;
  }

  .finding__score {
    text-align: center;
    background: ${PRINT_COLORS.surfaceMuted};
    padding: 0.5rem 0.75rem;
    border-radius: 8px;
    min-width: 50px;
  }

  .finding__score-value {
    font-family: 'DM Serif Display', Georgia, serif;
    font-size: 1.375rem;
    font-weight: 400;
    line-height: 1;
  }

  .finding__score-label {
    font-size: 0.55rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: ${PRINT_COLORS.inkLight};
    margin-top: 0.25rem;
  }

  .finding__content {
    display: flex;
    flex-direction: column;
    gap: 0.875rem;
  }

  .finding__description {
    font-size: 0.85rem;
    color: ${PRINT_COLORS.inkMuted};
    line-height: 1.65;
    padding-left: 0.75rem;
    border-left: 2px solid ${PRINT_COLORS.borderLight};
    font-style: italic;
  }

  .finding__action {
    font-size: 0.85rem;
    display: flex;
    gap: 0.5rem;
  }

  .finding__action-label {
    font-weight: 600;
    color: ${PRINT_COLORS.ink};
    flex-shrink: 0;
  }

  .finding__action-value {
    color: ${PRINT_COLORS.inkMuted};
    line-height: 1.5;
  }

  /* Field Box (for clause text) */
  .field-box {
    border: 1px solid ${PRINT_COLORS.borderLight};
    border-radius: 8px;
    background: ${PRINT_COLORS.surfaceMuted};
    padding: 1rem 1.125rem;
  }

  .field-box--replacement {
    border-left: 4px solid ${PRINT_COLORS.low.dot};
    background: ${PRINT_COLORS.low.bg};
  }

  .field-box__label {
    font-size: 0.65rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: ${PRINT_COLORS.inkLight};
    margin-bottom: 0.5rem;
  }

  .field-box__value {
    font-size: 0.85rem;
    line-height: 1.6;
    color: ${PRINT_COLORS.ink};
    white-space: pre-wrap;
  }

  /* Context/Recommendation Cards */
  .context-card {
    border: 1px solid ${PRINT_COLORS.borderLight};
    border-radius: 6px;
    padding: 1rem 1.25rem;
    margin-bottom: 0.75rem;
    background: ${PRINT_COLORS.surface};
  }

  .context-card__meta {
    display: flex;
    gap: 0.5rem;
    font-size: 0.7rem;
    color: ${PRINT_COLORS.inkLight};
    margin-bottom: 0.5rem;
  }

  .context-card__text {
    font-size: 0.875rem;
    color: ${PRINT_COLORS.ink};
    line-height: 1.6;
  }

  .rec-card {
    border: 1px solid ${PRINT_COLORS.borderLight};
    border-radius: 6px;
    padding: 1rem 1.25rem;
    margin-bottom: 0.75rem;
    background: ${PRINT_COLORS.surface};
  }

  .rec-card__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.5rem;
  }

  .rec-card__title {
    font-size: 0.9rem;
    font-weight: 600;
    color: ${PRINT_COLORS.ink};
  }

  .rec-card__due {
    font-size: 0.7rem;
    color: ${PRINT_COLORS.inkLight};
  }

  .rec-card__notes {
    font-size: 0.85rem;
    color: ${PRINT_COLORS.inkMuted};
    line-height: 1.6;
  }

  /* Ranked List */
  .ranked-list {
    list-style: none;
    padding: 0;
  }

  .ranked-item {
    display: flex;
    gap: 1rem;
    padding: 0.875rem 0;
    border-bottom: 1px solid ${PRINT_COLORS.borderLight};
  }

  .ranked-item:last-child {
    border-bottom: none;
  }

  .rank-badge {
    flex-shrink: 0;
    width: 26px;
    height: 26px;
    background: ${PRINT_COLORS.accentLight};
    color: ${PRINT_COLORS.accent};
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 0.75rem;
  }

  .ranked-item__content {
    flex: 1;
  }

  .ranked-item__title {
    font-weight: 600;
    color: ${PRINT_COLORS.ink};
    margin-bottom: 0.25rem;
  }

  .ranked-item__desc {
    font-size: 0.85rem;
    color: ${PRINT_COLORS.inkMuted};
    line-height: 1.5;
  }

  /* FAR Clauses */
  .far-list {
    border-collapse: collapse;
    width: 100%;
  }

  .far-list ul {
    display: table;
    width: 100%;
    border-collapse: collapse;
    list-style: none;
    margin: 0;
    padding: 0;
  }

  .far-list li {
    display: table-row;
  }

  .far-list li > * {
    display: table-cell;
    padding: 0.625rem 0.75rem;
    vertical-align: top;
    border-bottom: 1px solid ${PRINT_COLORS.borderLight};
  }

  .far-list li:last-child > * {
    border-bottom: none;
  }

  .far-list li:nth-child(even) > * {
    background: ${PRINT_COLORS.surfaceMuted};
  }

  .far-list__ref {
    width: 22%;
    font-size: 0.7rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.02em;
    color: ${PRINT_COLORS.inkMuted};
    line-height: 1.4;
  }

  .far-list__text {
    width: 25%;
    font-family: 'DM Mono', 'SF Mono', 'Consolas', monospace;
    font-size: 0.75rem;
    font-weight: 500;
    color: ${PRINT_COLORS.accent};
    line-height: 1.4;
  }

  .far-list__clause-text {
    width: 53%;
    font-size: 0.8rem;
    color: ${PRINT_COLORS.ink};
    line-height: 1.55;
  }

  .far-list__clause-text strong {
    font-weight: 600;
    color: ${PRINT_COLORS.ink};
  }

  /* Metrics Grid (SLIC Financial) */
  .metrics-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
    margin-bottom: 1.5rem;
  }

  .metric-box {
    background: ${PRINT_COLORS.surfaceMuted};
    border-radius: 6px;
    padding: 1rem;
  }

  .metric-box__label {
    font-size: 0.65rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: ${PRINT_COLORS.inkLight};
    margin-bottom: 0.375rem;
  }

  .metric-box__value {
    font-size: 1rem;
    color: ${PRINT_COLORS.ink};
    font-weight: 500;
  }

  .metric-box__value--savings {
    color: ${PRINT_COLORS.low.text};
    font-weight: 600;
  }

  /* Print media */
  @media print {
    @page {
      margin: 0.75in;
      size: Letter;
    }

    body {
      padding: 0;
      max-width: none;
    }

    p, li, dd {
      orphans: 3;
      widows: 3;
    }

    h1, h2, h3, h4, h5, h6,
    .section__header {
      page-break-after: avoid;
      break-after: avoid;
    }

    .card,
    .finding,
    .summary,
    .context-card,
    .rec-card,
    .section {
      page-break-inside: avoid;
      break-inside: avoid;
    }

    .stat-box,
    .badge,
    .field-box,
    .rank-badge,
    .dot {
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
  }
`

// ============================================================================
// HTML Generators
// ============================================================================

/**
 * Wrap content in a complete HTML document.
 */
export function wrapHtmlDocument(title: string, content: string, extraStyles = ''): string {
  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${escapeHtml(title)}</title>
  <style>${PRINT_BASE_STYLES}${extraStyles}</style>
</head>
<body>
  ${content}
</body>
</html>`
}

/**
 * Open a print dialog with generated HTML.
 */
export function openPrintWindow(html: string): void {
  const printWindow = window.open(
    '',
    '_blank',
    `width=${screen.width},height=${screen.height},fullscreen=yes,scrollbars=yes`
  )
  if (!printWindow) return

  printWindow.document.write(html)
  printWindow.document.close()

  printWindow.onload = () => {
    printWindow.focus()
    printWindow.print()
    printWindow.close()
  }
}
